/**
 * Redact All — one press, every file whose output is behind (VE-31006).
 *
 * The orchestrator only. What to burn is `buildRedactAllWorkList`'s answer,
 * how to burn one is `burnOneDocument`'s, and rebuilding the merged PDF is the
 * combine saga's. This decides the order, what a failure means, and whether
 * the merge follows.
 *
 * AN EMPTY WORK LIST IS A VALID PRESS when it carries a combine (VE-31796).
 * A merged PDF whose members are all current needs no burning, and the
 * progress modal is driven by this pass — so skipping it meant the longest
 * thing either Generate or Update starts happened in silence. The run opens,
 * reports the build, and the combine's own actions carry it to done.
 *
 * AND IT REFUSES A RELEASE IT CANNOT PUT RIGHT. The caller hands over the
 * members its own surface calls behind; a member in that set which the work
 * list skips — a burn already in flight, an upload-written redaction stub —
 * is one the combine would fold in at its PREVIOUS output, with nothing
 * failing to report. No run starts and no combine is dispatched in that case.
 *
 * SEQUENTIAL, following `saveAllDocumentsSaga` and for its reasons: each burn
 * is a mupdf WASM job plus several asset writes, and concurrent runs would
 * interleave the per-document SAVE and EXPORT actions into a state that
 * reports on whichever finished last.
 *
 * FAILURES DO NOT ABORT THE PASS. One file failing is no reason to abandon the
 * other nineteen — also `saveAllDocumentsSaga`'s rule. They are collected and
 * reported at the end, where the reviewer can see all of them at once instead
 * of nineteen toasts deep.
 *
 * LOGOUT STOPS IT DEAD. `burnOneDocument` bounds each file, and the pass reads
 * its verdict: a long run is exactly when a token expires, and continuing
 * would sign the remaining uploads as a user who is no longer there.
 *
 * NOTHING IS PERSISTED. The list was derived at the press and the run holds it
 * in memory, so a pass that dies with the tab leaves no job record to
 * reconcile — the files it finished are done, and pressing again produces the
 * remainder. See `redactAllWorkList`.
 */
import {
  call,
  cancel,
  fork,
  put,
  race,
  select,
  take,
  takeLeading,
} from 'typed-redux-saga/macro';

import { COMBINE_PROJECT_REQUEST } from '@redact-modules/projects/actions';
import { ensureSetUnlocked } from '@redact-modules/projects/setLockGate';
import { sagaIntl } from '@i18n';
import { enqueueSnackbar } from '@common-modules/snackbar';
import type { RedactAllEntry } from '@redact-modules/casemedia/redactAllWorkList';
import type { TDOId } from '@common-modules/universal/models/Brands';
import {
  CANCEL_REDACT_ALL,
  REDACT_ALL_FINISHED,
  REDACT_ALL_PROGRESS,
  REDACT_ALL_REQUEST,
  REDACT_ALL_STARTED,
} from '../actions';
import { selectSession, selectSuspendedSessions } from '../selectors';
import { selectBatesGovernanceFor } from '@redact-modules/projects/renderedBates';
import {
  selectWatermarkGovernanceFor,
  selectWatermarkProjectFor,
} from '@redact-modules/projects/renderedWatermarks';
import type { SessionState } from '../models';
import { burnOneDocument } from './exportPush';
import { buildHeadlessSession } from './headlessSession';

/** One file's outcome, for the summary the pass reports. */
/** The editor-scoped answers a pass must not re-read mid-run. */
export interface FrozenGovernance {
  readonly bates: ReturnType<ReturnType<typeof selectBatesGovernanceFor>>;
  readonly watermark: ReturnType<
    ReturnType<typeof selectWatermarkGovernanceFor>
  >;
  readonly watermarkProjectTdoId: TDOId | null;
}

export interface RedactAllFailure {
  readonly tdoId: string;
  readonly name: string | null;
  /**
   * `unreadable` is a file whose saved state could not be read at all;
   * `burn` is one that was read and failed to render or upload; `lostEdits`
   * is one whose unsaved work the editor no longer holds, which is a refusal
   * rather than a fault — burning it would publish a file the reviewer would
   * recognise as out of date.
   */
  readonly cause: 'unreadable' | 'burn' | 'lostEdits';
}

/**
 * The session to burn this file FROM, in order of how much it knows.
 *
 * The live session first, because the document on screen may hold edits
 * nothing has saved yet. Then a SUSPENDED one — the editor keeps a full
 * session for every document this visit has switched away from, and those can
 * hold unsaved edits too; `saveBeforeExport` persists whatever snapshot it is
 * handed, so passing one is what turns "saved first, then burned" into a
 * property of the pass rather than a separate Save All the reviewer has to
 * remember. Only then the saved asset.
 *
 * Getting this order wrong is silent: a suspended document burned from its
 * asset releases the file WITHOUT the edits sitting in memory, and the editor
 * goes on showing them as though they had been published.
 */
/**
 * What governs each file's numbering and marking, resolved once.
 *
 * All three of these resolve through the OPEN REQUEST
 * (`selectGoverningProjectId` → `selectEditorRequestFolder`), which returning
 * to the folder view clears. Resolved per file mid-pass they come back
 * `ungoverned`, and `ungoverned` is not an error state — it is the legitimate
 * answer for a document in no merged set, so nothing warns. The member burns
 * its own leftover per-file Bates stamp, which is the exact defect VE-30105
 * fixed ("two documents in one release came out numbered from the same place,
 * each reported current"), and draws none of the set's watermarks.
 */
function* freezeGovernanceAtPress(entries: readonly RedactAllEntry[]) {
  const byTdo = new Map<string, FrozenGovernance>();
  for (const entry of entries) {
    const tdoId = entry.tdoId as TDOId;
    byTdo.set(String(tdoId), {
      bates: yield* select(selectBatesGovernanceFor(tdoId)),
      watermark: yield* select(selectWatermarkGovernanceFor(tdoId)),
      watermarkProjectTdoId:
        (yield* select(selectWatermarkProjectFor(tdoId))) ?? null,
    });
  }
  return byTdo;
}

function* freezeSessionsAtPress(entries: readonly RedactAllEntry[]) {
  const live = yield* select(selectSession);
  const suspended = yield* select(selectSuspendedSessions);
  const held = new Map<string, SessionState>();
  for (const entry of entries) {
    const id = String(entry.tdoId);
    if (live && String(live.tdoId) === id) {
      held.set(id, live);
      continue;
    }
    const parked = suspended[id];
    if (parked) {
      held.set(id, parked);
    }
  }
  return held;
}

/**
 * The session to burn this file FROM.
 *
 * TAKEN FROM THE SNAPSHOT, never re-read. The editor's sessions are the
 * reviewer's unsaved work, and `CLOSE_DOCUMENT` empties `suspendedSessions`
 * while this pass — a root saga — carries on. Reading them per file meant a
 * reviewer who pressed the button and then went back to the folder view to
 * watch the rows had every remaining file burned from its SAVED asset, with
 * the edits they had just drawn thrown away, published, and folded into the
 * release. The list is frozen at the press; the sessions it names have to be
 * frozen with it.
 *
 * A MISS ON AN UNSAVED ENTRY IS FATAL FOR THAT FILE. The work list said this
 * document has edits nothing has saved; if the snapshot does not hold them
 * then they are gone, and burning the asset would publish a file the reviewer
 * would recognise as out of date. Failing it is also what suppresses the
 * merge, which is the right outcome for a release that would be missing
 * somebody's work.
 */
function* sessionFor(
  tdoId: TDOId,
  entry: RedactAllEntry,
  frozen: ReadonlyMap<string, SessionState>
) {
  const held = frozen.get(String(tdoId));
  if (held) {
    /**
     * A SESSION SOMEBODY HAS OPEN IS BY CONSTRUCTION A REVIEWED ONE — that
     * is what having it open means, and the burn renders whatever it holds
     * rather than the empty plan the builder would have made.
     *
     * So `unreviewed: false`, and it is load-bearing rather than a default.
     * An `includedUnworked` entry CAN land here: the reason only requires
     * that nothing is saved, and a clean open document satisfies that while
     * its session carries the upload stub's lexicon detections. Burned from
     * here it is not an empty plan, so stamping it "nobody reviewed this"
     * would describe a file this pass did not produce.
     */
    return { ok: true as const, session: held, unreviewed: false };
  }
  if (entry.reasons.includes('unsaved')) {
    return { ok: false as const, reason: 'lostEdits' as const };
  }
  // THE ONE ENTRY THAT MAY HAVE NOTHING SAVED. The builder refuses an
  // unworked file by default, and must: a list built off rows a moment stale
  // is not a reason to publish "reviewed, nothing needed" about a document
  // nobody opened. This reason is the reviewer's own answer to that, carried
  // from the dialog that asked them — see `includedUnworked`.
  return yield* call(buildHeadlessSession, tdoId, {
    allowUnworked: entry.reasons.includes('includedUnworked'),
  });
}

export function* handleRedactAllRequest(
  action: ReturnType<typeof REDACT_ALL_REQUEST>
) {
  const { entries, combine, alreadyCurrent, membersBehind } = action.payload;

  /**
   * THE SEVENTH SET-WIDE WRITE (VE-31538), and the one the first pass of
   * this work missed while gating the other six.
   *
   * With a `combine` payload this re-burns every member and rotates out
   * each one's previous audit CSV before handing off to the merge — so a
   * press that slipped past the UI reached further than any other, and the
   * refusal arrived only at the combine, after twenty files had been
   * rewritten. Gated here, nothing is touched.
   */
  if (combine && !(yield* ensureSetUnlocked(combine.projectTdoId))) {
    // NOTHING TO FINISH, so nothing is reported. The gate has already told
    // the reviewer why, and `REDACT_ALL_STARTED` — which creates the modal's
    // state — is dispatched below here, so there is no run in progress for a
    // summary to describe.
    //
    // Reporting one anyway LIED ABOUT THE PREVIOUS RUN. `redactAll` survives
    // a completed pass until the reviewer closes the modal (DISMISS_REDACT_ALL
    // only clears it at `phase: 'done'`), so a second press that this gate
    // refused rewrote that pass's summary in place: `burned: 0`, and
    // `mergeFailed` derived true because its `mergeProjectTdoId` was still
    // set. A run that redacted twenty files and rebuilt the release then read
    // as having done neither.
    //
    // The sibling refusal below — a member this pass cannot put right — has
    // always returned bare, for the same reason.
    return;
  }

  /**
   * MEMBERS THIS PASS CANNOT PUT RIGHT, and so must not release over.
   *
   * The caller's behind-set and the work list it handed us do not exclude
   * the same files — a burn already in flight, or an upload-written
   * redaction stub, keeps a member out of the list while the numbering and
   * marking checks still call it behind. Rebuilding with one of those in
   * the set folds its PREVIOUS output into the release, and nothing fails,
   * so the modal would report a success.
   *
   * The combine's own check cannot catch it: it refuses members with NO
   * output and merges a stale one silently.
   *
   * REFUSE RATHER THAN RELEASE. This is the one case where the press
   * genuinely cannot do what the banner asked for, and a release missing a
   * reviewer's redactions is the outcome this product exists to prevent.
   * Waiting is cheap; an unredacted page in a disclosure is not.
   */
  const willBurn = new Set(entries.map((entry) => String(entry.tdoId)));
  const unfixable = (membersBehind ?? []).filter(
    (id) => !willBurn.has(String(id))
  );
  if (entries.length === 0 && !combine) {
    return;
  }
  if (combine && unfixable.length > 0) {
    yield* put(
      enqueueSnackbar({
        message: sagaIntl().formatMessage(
          {
            id: 'mergedDocumentMemberNotReady',
            defaultMessage:
              '{count, plural, one {# file is} other {# files are}} still being redacted. The merged PDF was not rebuilt — try again once they finish.',
          },
          { count: unfixable.length }
        ),
        variant: 'warning',
        autoHideDuration: 10000,
      })
    );
    return;
  }

  yield* put(
    REDACT_ALL_STARTED({
      total: entries.length,
      entries,
      mergeProjectTdoId: combine ? String(combine.projectTdoId) : null,
      alreadyCurrent: alreadyCurrent ?? 0,
      // THE MEMBERS THE COMBINE IS ACTUALLY CARRYING, less the ones it is
      // allowed to leave out — read off the payload about to be dispatched
      // rather than taken as a separate count that could contradict it.
      mergeFileCount: combine
        ? Math.max(
            0,
            combine.members.length - (combine.excludable?.length ?? 0)
          )
        : null,
    })
  );

  /**
   * NOTHING TO BURN, BUT STILL SOMETHING TO BUILD (VE-31796).
   *
   * A merged PDF generated from members that are all current, or rebuilt
   * after one was removed from the request, needs no pass at all — and this
   * used to return before `REDACT_ALL_STARTED`, so the combine was dispatched
   * straight from the button and the reviewer watched nothing happen. The
   * merge takes as long as it takes whether or not a burn preceded it; the
   * account of it should not depend on that.
   *
   * It skips the rest of the pass rather than falling through it: the loop,
   * the frozen sessions and the stop watcher all have nothing to do here, and
   * `complete` is defined in terms of a burn count that is vacuously met.
   */
  if (entries.length === 0 && combine) {
    /**
     * `FINISHED` FIRST, THEN THE COMBINE — the order is load-bearing.
     *
     * `put` is synchronous: the combine's watcher runs inside this dispatch
     * and gets as far as its first blocking effect, and its "select at least
     * two documents" refusal has none — it is two puts and a return. So
     * dispatching the combine first delivered `COMBINE_PROJECT_FAILURE`
     * while the run was still `burning`, where the reducer ignores it, and
     * the `merging` set a line later had nothing left to move it on. The
     * modal then sat on "Building the merged PDF" for the life of the tab,
     * Dismiss did nothing (it clears only a `done` run), and every Update on
     * the page stayed disabled behind `redactAllInFlight`.
     *
     * Reachable: a merged set whose live membership has dropped to one, which
     * is exactly the state the banner offers Update for.
     */
    yield* put(
      REDACT_ALL_FINISHED({
        burned: 0,
        failures: [],
        loggedOut: false,
        cancelled: false,
        // The combine's own actions carry the run from here to `done`.
        merging: true,
      })
    );
    yield* put(COMBINE_PROJECT_REQUEST(combine));
    return;
  }

  /**
   * EVERYTHING EDITOR-SCOPED, READ ONCE, HERE.
   *
   * A single Redact File is one burn against one editor state, which is why
   * `stateAtClick` freezes the principal and nothing else needed freezing. A
   * pass is N burns over minutes, and both of these are destroyed by things
   * the reviewer does WHILE it runs — closing the editor empties the
   * suspended sessions, and returning to the folder view clears the open
   * request that governance is resolved through. `renderedWatermarks` says so
   * outright: "A caller that outlives the editor (a burn-in) must read this
   * AT THE CLICK: later it is undefined."
   *
   * Read per file, a reviewer who pressed the button and went back to watch
   * the rows got the rest of the release burned with each member's own
   * leftover per-file Bates stamp and none of the set's watermarks — silently,
   * because every warning in the burn is keyed on a status this does not
   * produce.
   */
  const frozenSessions = yield* call(freezeSessionsAtPress, entries);
  const frozenGovernance = yield* call(freezeGovernanceAtPress, entries);

  const failures: RedactAllFailure[] = [];
  let burned = 0;
  let loggedOut = false;
  let cancelled = false;
  /**
   * A STOP PRESSED BETWEEN FILES IS STILL A STOP.
   *
   * The per-file race below only listens while a burn is in flight, so a
   * press landing in the gap — while the next file's session is being built —
   * fell on the floor and the pass carried on. The watcher holds the press
   * until the loop reads it.
   */
  const stopWatcher = yield* fork(function* watchForStop() {
    yield* take(CANCEL_REDACT_ALL);
    cancelled = true;
  });

  /**
   * THE PASS ALWAYS ENDS, however it ends.
   *
   * `REDACT_ALL_FINISHED` was the only exit from `phase: 'burning'`, so
   * anything that threw outside `burnOneDocument`'s own catch — a selector, a
   * reducer call in the headless builder — left the run in flight forever:
   * the button disabled, `DISMISS_REDACT_ALL` refusing to clear a pass that
   * was not `done`, and every later burn's success toast suppressed as part
   * of a run that had stopped. `keepAlive` does not help; it re-arms the
   * watcher, not the run.
   */
  try {
    for (let index = 0; index < entries.length; index += 1) {
      if (cancelled) {
        // Pressed while the previous file was being wrapped up, or while this
        // one's session was being built.
        break;
      }
      const entry = entries[index] as RedactAllEntry;
      const tdoId = entry.tdoId as TDOId;
      yield* put(
        REDACT_ALL_PROGRESS({ tdoId: entry.tdoId, name: entry.name, index })
      );
      const governance = frozenGovernance.get(String(tdoId));
      const built = yield* call(sessionFor, tdoId, entry, frozenSessions);
      if (!built.ok) {
        // Including `unworked`, which the list is supposed to have excluded
        // already — the rows it was built from can be a moment stale, and a
        // file that turns out to have no saved work must not be published on
        // the strength of a snapshot.
        failures.push({
          tdoId: entry.tdoId,
          name: entry.name,
          cause: built.reason === 'lostEdits' ? 'lostEdits' : 'unreadable',
        });
        continue;
      }
      /**
       * EVERY FILE RACES THE STOP BUTTON (VE-31309).
       *
       * Cancelling has to reach the file in flight, not merely the queue
       * behind it — a twenty-file run started by mistake is otherwise
       * twenty published outputs with nothing to do but watch. Losing the
       * race cancels the burn's whole call, so the upload at the end of it
       * never happens and nothing is written for this file.
       *
       * It cannot ABORT the burn: the worker has no abort path, so the WASM
       * job may run to completion in the background with its result
       * discarded. The one exposed edge is a cancel landing mid-upload,
       * where the request may still reach the server; what that creates is
       * a valid redacted output, just one this pass did not count.
       *
       * Files already finished stay published. There is no unpublishing a
       * redacted output, and the button must not imply there is.
       */
      const { outcome, stopped } = yield* race({
        /**
         * WHAT WAS BUILT, not what the list expected to build.
         *
         * The entry's reason is the PERMISSION — derived from rows fetched
         * at the press, where being wrong costs nothing because the builder
         * re-checks. `built.unreviewed` is the FACT, read off the asset
         * moments ago, and it is what the burn records: the writer marker on
         * the forced save, the audit event, the output's details, the
         * filename. Passing the permission instead meant a colleague's save
         * landing mid-pass was rendered correctly and then recorded as "no
         * redactions, nobody reviewed this" — in the CSV that ships with the
         * release (review round 3, T2).
         */
        outcome: call(
          burnOneDocument,
          tdoId,
          built.session,
          governance,
          built.unreviewed
        ),
        stopped: take(CANCEL_REDACT_ALL),
      });
      if (stopped) {
        cancelled = true;
        break;
      }
      if (outcome === 'loggedOut') {
        loggedOut = true;
        break;
      }
      if (outcome === 'failed') {
        failures.push({ tdoId: entry.tdoId, name: entry.name, cause: 'burn' });
      } else if (outcome === 'done') {
        burned += 1;
      }
      // `alreadyRunning` IS INCOMPLETE. Another run is producing this file's
      // next output, so the pass neither burned it nor failed at it — but it
      // did not bring it up to date either, and that is what `complete` has to
      // mean. Counted as burned it let the merge rebuild over the file's
      // PREVIOUS output while its own burn was still running, leaving the
      // just-rebuilt release stale the moment that burn landed.
    }
  } catch (err) {
    // Whatever it was, this pass is over and did not finish. Recording it as
    // a failure is also what suppresses the merge, which is the right
    // outcome for a release built from a run that fell over.
    console.error('[redact] redact-all pass aborted', err);
    failures.push({
      tdoId: '',
      name: null,
      cause: 'burn',
    });
  }

  /**
   * THE MERGE ONLY OVER A COMPLETE PASS.
   *
   * A merged PDF is a release artifact. Rebuilding it from a run that could
   * not burn three of its members would publish a document whose contents the
   * reviewer was never shown — the combine CAN record members as excluded, but
   * a release quietly missing records is the one outcome this product must not
   * produce. A stale merged PDF the reviewer knows about beats a fresh one
   * that dropped three files.
   *
   * So: every file burned, nothing failed, nobody logged out. Otherwise the
   * previous merged PDF stays exactly where it is and the summary says why.
   */
  // The pass is past the point where stopping means anything: what remains
  // is a combine that publishes or does not. Left running, the watcher would
  // also outlive the pass and swallow the first press of the NEXT one.
  yield* cancel(stopWatcher);

  const touchesMergeSet = entries.some((entry) => entry.isMember);
  // CANCELLED IS INCOMPLETE, and that is the whole safety of the button: an
  // incomplete pass does not merge, so stopping one leaves the previous
  // merged PDF exactly where it was rather than publishing a release built
  // from the files that happened to finish first.
  //
  // `!cancelled` is REDUNDANT TODAY and kept deliberately: a stop breaks
  // before the file in flight is counted, so `burned` can never reach
  // `entries.length` on a cancelled pass. It is here because the count's
  // meaning has drifted before — see the `alreadyRunning` note above, where
  // treating an uncounted file as burned let the merge rebuild over an
  // output that was still being written. Stating the condition directly
  // means the next such drift cannot quietly re-enable the merge.
  const complete =
    failures.length === 0 &&
    !loggedOut &&
    !cancelled &&
    burned === entries.length;
  const merging = complete && touchesMergeSet && !!combine;

  // THE SAME ORDER AS THE SHORT-CIRCUIT ABOVE, and for the same reason: the
  // combine can refuse synchronously, and a refusal that arrives before the
  // run reaches `merging` is a refusal the run never hears.
  yield* put(
    REDACT_ALL_FINISHED({
      burned,
      failures,
      loggedOut,
      cancelled,
      merging,
    })
  );
  if (merging && combine) {
    yield* put(COMBINE_PROJECT_REQUEST(combine));
  }

  if (loggedOut) {
    // No toast: there is nobody signed in to read it.
    console.warn('[redact] redact-all abandoned: logged out mid-pass');
    return;
  }
  if (failures.length > 0) {
    yield* put(
      enqueueSnackbar({
        message: sagaIntl().formatMessage(
          {
            id: 'redactAllPartialFailure',
            defaultMessage:
              '{failed, plural, one {# file} other {# files}} could not be redacted. {merged, select, true {The merged PDF was left as it was.} other {}}',
          },
          { failed: failures.length, merged: String(touchesMergeSet) }
        ),
        variant: 'warning',
        autoHideDuration: 10000,
      })
    );
  }
}

export function* redactAllSaga() {
  // `takeLeading`: a second press while a pass is running is the same pass
  // being asked for twice. Dropping it is right — the work list it would
  // compute is the one already being worked through, and two passes would
  // race on every version number in it.
  yield* takeLeading(REDACT_ALL_REQUEST, handleRedactAllRequest);
}
